import { ApolloClient } from '@apollo/client';
import { REMOVE_USERS_FID } from './api/PushNotificationQuery';

// What this browser last sent to the backend, so the same FID / permission isn't sent again on every login.
// Cleared with the rest of localStorage on logout.
const SAVED_FID_KEY = 'pushSavedFid';
const SAVED_PERMISSION_KEY = 'pushSavedAllowNotifications';

interface SavedFid {
  userId: string;
  fid: string;
}

interface SavedPermission {
  userId: string;
  allowed: boolean;
}

const read = <T,>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};

export const getSavedFid = () => read<SavedFid>(SAVED_FID_KEY);
export const setSavedFid = (value: SavedFid) => localStorage.setItem(SAVED_FID_KEY, JSON.stringify(value));
export const clearSavedFid = () => localStorage.removeItem(SAVED_FID_KEY);

export const getSavedPermission = () => read<SavedPermission>(SAVED_PERMISSION_KEY);
export const setSavedPermission = (value: SavedPermission) => localStorage.setItem(SAVED_PERMISSION_KEY, JSON.stringify(value));

/**
 * On logout, while the user is still authenticated: removes this browser's FID from the backend (removeUsersFid)
 * and unregisters the browser from FCM, so no notifications arrive after logout.
 * Does nothing if this browser never registered; never blocks logout (failures are ignored).
 */
export const clearPushRegistrationOnLogout = async (client: ApolloClient<object>) => {
  const saved = getSavedFid();
  if (!saved) return;
  clearSavedFid();
  await Promise.allSettled([
    client.mutate({ mutation: REMOVE_USERS_FID, variables: { fid: saved.fid } }),
    import('./messaging').then(({ stopPushRegistration }) => stopPushRegistration()),
  ]);
};
